import { Box, SelectChangeEvent, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { PoolIconButton, PoolSelect, PoolSelectOption, useIsMobile } from '@synergy/components-next';
import { SortingArrowLeftIcon } from '@synergy/theme-next';
import { useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as SearchHistoryActions from '../../../../store/search-history-actions';
import { ROUTES } from '../../../../utils/routes';
import { SearchAutocomplete } from './search-autocomplete/search-autocomplete';
import { categoriesSelector } from '../../../../store/categories-selectors';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';

const StyledSelect = styled(PoolSelect)(({ theme }) => ({
    height: 48,
    backgroundColor: theme.palette.common.white,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    maxWidth: 175,

    '& .MuiOutlinedInput-input': {
        padding: '13px 16px',
    },
    '&& .MuiOutlinedInput-input.MuiSelect-select': {
        paddingRight: 44,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },

    '& svg': {
        right: 16,
    },
}));

const Wrapper = styled(Box, {
    shouldForwardProp: prop => prop !== 'focus',
})<{ focus: boolean }>(({ theme, focus }) => ({
    display: 'flex',
    flex: 1,
    border: `2px solid ${focus ? theme.palette.primary.main : 'transparent'}`,
    borderRadius: 6,
    height: 52, // 48px + 4px border

    '&:hover': {
        borderColor: !focus ? theme.palette.grey[800] : theme.palette.primary.main,
    },

    [theme.breakpoints.up('sm')]: {
        height: 54, // 48px + 6px border
        margin: '-4px',
        border: `3px solid ${focus ? theme.palette.primary.main : 'transparent'}`,
        borderRadius: 6,
    },
}));

export const HeaderSearchBar = (): ReactElement => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState('');
    const categories = useAppSelector(state => categoriesSelector(state)());
    const [focus, setFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const newsDetailMatch = useMatch(ROUTES.news.detail);
    const searchResultsMatch = useMatch(ROUTES.searchPage);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // Reset category to "All categories" if it was changed outside of the searchbar
        if (searchParams.get(FilterParams.CategoryId) !== selectedCategoryId.toString()) {
            setSelectedCategoryId(0);
        }
        if (searchResultsMatch) {
            setSearchTerm(searchParams.get(FilterParams.Search) ?? '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const categorySelectOptions: PoolSelectOption<number>[] = categories.map(category => ({
        value: category.id,
        displayValue: category.name,
    }));

    // HINT: remove category searchFilter when value 0 with || operator (api call)
    categorySelectOptions.unshift({
        value: 0,
        displayValue: intl.formatMessage({ id: 'app.searchBar.allCategories' }),
    });

    const handleCategoryChange = (event: SelectChangeEvent<unknown>): void => {
        setSelectedCategoryId(event.target.value as number);
        // Check why its not working (probably nice to have?)
        inputRef.current?.focus();
    };

    const handleSearchCommit = (event: KeyboardEvent): void => {
        if (event.key !== 'Enter') {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        for (const param in FilterParams) {
            nextSearchParams.delete(FilterParams[param as keyof typeof FilterParams]);
        }

        if (selectedCategoryId !== 0) {
            nextSearchParams.set(FilterParams.CategoryId, selectedCategoryId.toString());
        }
        if (searchTerm) {
            nextSearchParams.set(FilterParams.Search, searchTerm);
            dispatch(SearchHistoryActions.searchHistoryAdded(searchTerm.toLowerCase()));
        }
        // Only load search results if *any* filter/search is set
        if (Array.from(nextSearchParams).length > 0) {
            navigate(
                {
                    pathname: ROUTES.searchPage,
                    search: nextSearchParams.toString(),
                },
                { state: { isSearchFromEnter: !!searchTerm } }
            );
        }
    };

    return (
        <Wrapper focus={focus}>
            {!isMobile && (
                <Box>
                    <StyledSelect
                        value={selectedCategoryId}
                        onChange={handleCategoryChange}
                        options={categorySelectOptions}
                        placeholder={intl.formatMessage({ id: 'app.select.allCategories' })}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                        }}
                        onBlur={() => setFocus(false)}
                        onFocus={() => setFocus(true)}
                    />
                </Box>
            )}
            {isMobile && newsDetailMatch && (
                <PoolIconButton onClick={() => navigate(ROUTES.news.base)} size="large">
                    <SortingArrowLeftIcon sx={{ color: theme => theme.palette.common.white, fontSize: 24 }} />
                </PoolIconButton>
            )}
            <SearchAutocomplete
                sx={{ flex: 1 }}
                inputRef={inputRef}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyUp={handleSearchCommit}
                onBlur={() => setFocus(false)}
                onFocus={() => setFocus(true)}
                onClear={() => setSearchTerm('')}
            />
        </Wrapper>
    );
};
